import { getScreenNowPlaying } from '../../utils/screenActivity';
export const parseHdmiPort = (value) => {
  const match = String(value || '').trim().toLowerCase().match(/(?:(?:hdmi|input)\s*)?(1[0-6]|[1-9])\b/);
  return match ? Number(match[1]) : null;
};

export const findSharedScreen = (screens = [], deviceId) => {
  const normalizedDeviceId = Number(deviceId);
  return (Array.isArray(screens) ? screens : []).find((screen) => (
    Number(screen?.televisionDeviceId) === normalizedDeviceId
    || (screen?.playerDeviceIds || []).some((id) => Number(id) === normalizedDeviceId)
    || (screen?.players || []).some((player) => playerMatchesDevice(player, normalizedDeviceId))
  )) || null;
};

const playerMatchesDevice = (player, deviceId) => Number(player.deviceId || player.id) === Number(deviceId)
  || (Boolean(player.linkedDeviceId) && Number(player.linkedDeviceId) === Number(deviceId));

export const resolveScreenContext = (screens, deviceId, screenId) => {
  const matches = (screens || []).filter((screen) => Number(screen.televisionDeviceId) === Number(deviceId)
    || (screen.playerDeviceIds || []).some((id) => Number(id) === Number(deviceId))
    || (screen.players || []).some((player) => playerMatchesDevice(player, deviceId)));
  return matches.find((screen) => Number(screen.id) === Number(screenId)) || (matches.length === 1 ? matches[0] : null);
};

export const buildSharedScreenTargets = ({ screens = [], targets = [], selectedDeviceId, selectedScreenId, requireScreenContext = false }) => {
  const screen = requireScreenContext ? resolveScreenContext(screens, selectedDeviceId, selectedScreenId) : findSharedScreen(screens, selectedDeviceId);
  const targetById = new Map((Array.isArray(targets) ? targets : []).map((target) => [Number(target.id), target]));
  if (!screen) {
    // A shared source still needs an explicit Screen; standalone devices have no input to activate.
    if (requireScreenContext && findSharedScreen(screens, selectedDeviceId)) return [];
    const current = targetById.get(Number(selectedDeviceId));
    return current && !isAudioEquipment(current) ? [{ target: current, screen: null, expectedInput: null, switchRequired: false, switchReady: true }] : [];
  }
  const playerById = new Map((screen.players || []).map((player) => [Number(player.deviceId || player.id), player]));
  const candidateIds = Array.from(new Set([
    ...(screen.playerDeviceIds || []).map(Number),
    ...(screen.players || []).map((player) => Number(player.deviceId || player.id)),
    Number(screen.televisionDeviceId),
  ].filter(Boolean)));
  return candidateIds.flatMap((deviceId) => {
    const target = targetById.get(deviceId);
    if (!target) return [];
    const player = playerById.get(deviceId);
    if (!isVideoConnection(player, target)) return [];
    const switchRequired = deviceId !== Number(selectedDeviceId) && !playerMatchesDevice(player || {}, selectedDeviceId);
    return [{
      target,
      screen,
      expectedInput: player?.expectedInput || null,
      switchRequired,
      switchReady: requireScreenContext ? Boolean(screen.id) : !switchRequired || Boolean(screen.televisionDeviceId && parseHdmiPort(player?.expectedInput)),
    }];
  });
};

export const buildTargetTree = ({ screens = [], targets = [], reports = {}, liveStates = {}, now = Date.now() }) => {
  const targetById = new Map((targets || []).map((target) => [Number(target.id), target]));
  const included = new Set();
  const branches = (screens || []).flatMap((screen) => {
    const parent = targetById.get(Number(screen.televisionDeviceId)) || null;
    const children = (screen.players || []).flatMap((player) => {
      const target = targetById.get(Number(player.deviceId || player.id));
      if (!target || !isVideoConnection(player, target)) return [];
      included.add(Number(target.id));
      return [{ target, expectedInput: player.expectedInput || null }];
    });
    if (!parent && !children.length) return [];
    if (parent) included.add(Number(parent.id));
    const activity = getScreenNowPlaying(screen, targetById, liveStates,
      reports[Number(screen.televisionDeviceId)] || liveStates[screen.televisionDeviceId]?.state?.tvInputManager, now);
    const activeDeviceId = activity.confirmed ? Number(activity.activeDeviceId) || null : null;
    // Presentation order only; configured connection roles remain untouched.
    children.sort((a, b) => Number(Number(b.target.id) === activeDeviceId) - Number(Number(a.target.id) === activeDeviceId));
    return [{
      id: screen.id,
      name: screen.name || parent?.name || 'Screen',
      parent,
      children,
      activeDeviceId,
    }];
  });
  const excluded = new Set((screens || []).flatMap((screen) => (screen.players || [])
    .filter((player) => !isVideoConnection(player, targetById.get(Number(player.deviceId || player.id))))
    .map((player) => Number(player.deviceId || player.id))));
  const ungrouped = (targets || []).filter((target) => !included.has(Number(target.id))
    && !excluded.has(Number(target.id)) && !isAudioEquipment(target));
  return { branches, ungrouped };
};
import { isAudioEquipment, isVideoConnection } from '../../utils/equipment';
